import { useContext, useRef } from 'react';
import { useMapEvents } from 'react-leaflet';
import { buscarPorCoordenada, processarDadosPropriedade } from '../services/PropertyService';
import { propertyApi } from '../services/BaseApiService.jsx';
import { MapContext } from '../contexts/MapContext';
import { usePropertyFilters } from '../contexts/PropertyFilterContext';
import { resetGlobalContext } from '../utils/contextResetUtils';
import GeometryService from '../services/GeometryService.js';
import { getMunicipioPorCoordenadas } from '../services/MunicipalitiesService';
import { trackPropertyProgress } from '../services/PropertyProgressTracker';

/**
 * Hook para lidar com eventos de clique no mapa:
 * busca propriedades nas coordenadas do clique e atualiza o contexto.
 * Versão simplificada que assume que o backend retorna dados completos e consolidados.
 */
export default function useMapClick() {
  // Acessar contexto para atualização dos dados
  const mapContext = useContext(MapContext);
  const {
    setPropertyData,
    setGeometries,
    setThemeGeometries,
    setLoading,
    setError,
    setMarker,
    setActiveLayers,
    _setSidebarOpen,
    setProcessStatus,
    setRetryProcessingSearch,
  } = mapContext;

  // Acessar contexto de filtros para resetar
  const filterContext = usePropertyFilters();
  // Controle do tracker SSE para transição amarelo->azul.
  // pollingRef guarda a função `cancel()` retornada por trackPropertyProgress.
  const pollingRef = useRef(null);
  const clearPolling = () => {
    if (pollingRef.current) {
      try {
        pollingRef.current();
      } catch (_) {
        /* no-op */
      }
      pollingRef.current = null;
    }
  };

  const mergeMunicipioTheme = (themeGeometries, municipioFeature) => {
    if (!municipioFeature) return themeGeometries || {};
    return { ...(themeGeometries || {}), Município: [municipioFeature] };
  };

  const mergeMunicipioActiveLayer = (layers, municipioFeature) => {
    if (!municipioFeature) return layers || {};
    return { ...(layers || {}), Município: true };
  };

  useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;
      let municipioFeature = null;

      try {
        // Inicia o estado de carregamento
        setError(null);
        const DEBUG = import.meta.env.VITE_DEBUG === 'true';

        // Cancela qualquer polling anterior (novo clique)
        clearPolling();
        setProcessStatus(null);
        setRetryProcessingSearch(null);

        // Limpeza imediata de estados ao iniciar um novo clique de busca
        // Preserva o marcador (resetMarker=false), pois vamos definir um novo imediatamente abaixo
        try {
          resetGlobalContext(mapContext, filterContext, false, false);
        } catch (resetError) {
          if (DEBUG)
            console.warn(
              '[useMapClick] Falha ao limpar contexto imediatamente, aplicando fallback:',
              resetError,
            );
          if (mapContext.setPropertyData) mapContext.setPropertyData(null);
          if (mapContext.setGeometries) mapContext.setGeometries([]);
          if (mapContext.setThemeGeometries)
            mapContext.setThemeGeometries(mergeMunicipioTheme({}, municipioFeature));
          // Não limpar o marcador aqui; será definido a seguir
        }

        // Cria marcador intermediário (amarelo) imediatamente para feedback visual
        // Usamos a flag processing:true para o SimpleMapView renderizar o ícone amarelo
        setMarker({
          position: [lat, lng],
          processing: true,
          found: null, // indefinido durante o processamento
          data: null,
        });

        try {
          municipioFeature = await getMunicipioPorCoordenadas(lat, lng);
          if (municipioFeature) {
            setThemeGeometries(mergeMunicipioTheme({}, municipioFeature));
            setActiveLayers((prev) => mergeMunicipioActiveLayer(prev, municipioFeature));
          }
        } catch (_) {
          municipioFeature = null;
        }

        // Handler de processamento de resultado que será chamado pelo método buscar
        const handleResult = (propertyData, geometryData, extraData) => {
          // Se não houve propertyData (falha técnica), marcador vermelho
          if (!propertyData) {
            setMarker({ position: [lat, lng], processing: false, found: false, data: null });
            clearPolling();
            try {
              resetGlobalContext(mapContext, filterContext, false, false);
            } catch (_) {
              if (mapContext.setPropertyData) mapContext.setPropertyData(null);
              if (mapContext.setGeometries) mapContext.setGeometries([]);
              if (mapContext.setThemeGeometries)
                mapContext.setThemeGeometries(mergeMunicipioTheme({}, municipioFeature));
            }
            return;
          }

          // Sucesso (com ou sem geometrias): manter amarelo até timeline indicar process_succeeded
          // Preserva o file_id (como filename.json) no marker.properties para clique posterior (cache-first)
          const fid =
            propertyData?._metadata?.file_id ||
            (extraData?.filename ? String(extraData.filename).replace(/\.json$/, '') : null);
          const filenameProp = fid ? `${fid}.json` : null;
          const acceptedStatus = String(extraData?.search_accepted_status || '').toLowerCase();
          const isImmediateReady = acceptedStatus === 'succeeded';
          setMarker({
            position: [lat, lng],
            processing: !isImmediateReady,
            found: isImmediateReady ? true : null,
            data: null,
            properties: filenameProp ? { filename: filenameProp } : undefined,
          });

          if (isImmediateReady) {
            setProcessStatus(null);
            setRetryProcessingSearch(null);
          } else {
            setProcessStatus({
              file_id: fid || null,
              request_id: extraData?.request_id || null,
              pending_count: null,
              domains_processed_count: 0,
              domains_processed: [],
              final_status: null,
              last_status: 'processing_started',
              can_retry: false,
            });
            setRetryProcessingSearch(() => async () => {
              try {
                clearPolling();
                setProcessStatus((prev) => ({
                  ...(prev || {}),
                  final_status: null,
                  can_retry: false,
                  last_status: 'retry_requested',
                }));
                await buscarPorCoordenada(lat, lng, setPropertyData, setLoading, handleResult, {
                  force_refresh: true,
                });
              } catch (_) {
                // mantém status atual
              }
            });
          }

          // Se já houver geometrias no payload inicial, renderizá-las imediatamente e ativar camadas
          const hasGeometries = geometryData?.geometries?.length > 0;
          if (hasGeometries) {
            const newActiveLayers = {};
            const standardLayers = [
              'ÁREA DO IMÓVEL',
              'SIGEF',
              'ÁREA CONSOLIDADA',
              'VEGETAÇÃO NATIVA',
              'RESERVA LEGAL',
              'APP',
              'HIDROGRAFIA',
              'ÁREA DE SERVIDÃO',
              'ÁREA DE POUSIO',
              'USO RESTRITO',
            ];
            standardLayers.forEach((layer) => {
              newActiveLayers[layer] = true;
            });
            Object.keys(geometryData?.themeGeometries || {}).forEach((tema) => {
              if (
                geometryData.themeGeometries[tema] &&
                geometryData.themeGeometries[tema].length > 0
              ) {
                newActiveLayers[tema] = true;
              }
            });
            setActiveLayers(mergeMunicipioActiveLayer(newActiveLayers, municipioFeature));
            setPropertyData(propertyData);
            setGeometries(geometryData.geometries);
            setThemeGeometries(mergeMunicipioTheme(geometryData.themeGeometries, municipioFeature));
          }

          // Iniciar polling no endpoint de timeline até process_status=process_succeeded
          const fileIdForPolling = fid;
          if (!fileIdForPolling) {
            if (DEBUG)
              console.warn(
                '[useMapClick] file_id ausente; não é possível iniciar polling de timeline',
              );
            return;
          }
          if (!isImmediateReady && !pollingRef.current) {
            // SSE via consolidate-service `/events/status/{fid}` (D-SL-02).
            // Substitui polling em /api/v1/process/{fid} (depreciado em
            // 2026-05-23 com refactor track/SSE — F-PLAT-REDIS-09).
            pollingRef.current = trackPropertyProgress(fileIdForPolling, {
              requestId: extraData?.request_id || null,
              onSnapshot: (snapshot) => setProcessStatus(snapshot),
              onTerminal: async ({ finalStatus, failed }) => {
                if (failed) {
                  clearPolling();
                  return;
                }
                if (finalStatus !== 'process_succeeded' && finalStatus !== 'cache_hit') {
                  return;
                }
                try {
                  const jsonRes = await propertyApi.get(`/api/v1/state/${fileIdForPolling}`);
                  const consolidated = jsonRes?.data;
                  const propertyDataFinal = processarDadosPropriedade(consolidated);
                  const geometryFinal =
                    GeometryService.processPropertyGeometries(propertyDataFinal);
                  setPropertyData(propertyDataFinal);
                  setGeometries(geometryFinal.geometries || []);
                  setThemeGeometries(
                    mergeMunicipioTheme(geometryFinal.themeGeometries || {}, municipioFeature),
                  );
                  try {
                    const newActiveLayers = {};
                    const standardLayers = [
                      'ÁREA DO IMÓVEL',
                      'SIGEF',
                      'ÁREA CONSOLIDADA',
                      'VEGETAÇÃO NATIVA',
                      'RESERVA LEGAL',
                      'APP',
                      'HIDROGRAFIA',
                      'ÁREA DE SERVIDÃO',
                      'ÁREA DE POUSIO',
                      'USO RESTRITO',
                    ];
                    standardLayers.forEach((layer) => {
                      newActiveLayers[layer] = true;
                    });
                    Object.keys(geometryFinal?.themeGeometries || {}).forEach((tema) => {
                      if (geometryFinal.themeGeometries[tema]?.length > 0)
                        newActiveLayers[tema] = true;
                    });
                    setActiveLayers(mergeMunicipioActiveLayer(newActiveLayers, municipioFeature));
                  } catch (_) {
                    /* no-op */
                  }
                } catch (e) {
                  if (DEBUG)
                    console.warn('[useMapClick] Falha ao atualizar JSON final:', e?.message || e);
                }
                clearPolling();
                setProcessStatus((prev) => ({
                  ...(prev || {}),
                  pending_count: 0,
                  last_status: finalStatus,
                  final_status: finalStatus,
                  can_retry: false,
                }));
                setMarker({
                  position: [lat, lng],
                  processing: false,
                  found: true,
                  data: null,
                  properties: { filename: `${fileIdForPolling}.json` },
                });
              },
            });
          }
          return;
        };

        // Chama o serviço unificado para buscar propriedade por coordenada
        // Passa os setters do contexto para permitir atualização direta
        await buscarPorCoordenada(lat, lng, setPropertyData, setLoading, handleResult);
      } catch (error) {
        setError(error.message || 'Falha ao buscar dados da propriedade');

        // Define marcador vermelho no caso de erro e encerra processing
        setMarker({
          position: [lat, lng],
          processing: false,
          found: false,
          data: null,
        });

        setPropertyData(null);
        setGeometries([]);
        setThemeGeometries(mergeMunicipioTheme({}, municipioFeature));
        setProcessStatus(null);
        setRetryProcessingSearch(null);
        setLoading(false);
      }
    },
  });

  /**
   * Função auxiliar para extrair geometrias do objeto de propriedade
   * @param {Object} property - Dados da propriedade
   * @returns {Object} Objeto com geometrias por fonte e por tema
   */
  function _extrairGeometrias(property) {
    if (!property) return { geometries: [], themeGeometries: {} };

    const geometrias = [];
    const temaGeometrias = {}; // Organizada por tema para o LayerGroupManager

    // Obtém propriedades da estrutura correta
    const properties = property.properties || property;

    if (!properties) {
      return { geometries: [], themeGeometries: {} };
    }

    // Extrai geometrias do CAR (usando nova estrutura de API)
    if (properties.car && Array.isArray(properties.car)) {
      properties.car.forEach((carItem, _index) => {
        if (!carItem || !carItem.themes || !Array.isArray(carItem.themes)) {
          return;
        }

        // Processar cada tema do item CAR
        carItem.themes.forEach((theme, _themeIndex) => {
          if (!theme || !theme.nome_tema || !theme.geojson) {
            return;
          }

          try {
            // Converte string GeoJSON para objeto, se necessário
            const parsedGeojson = GeometryService.processGeoJSON(theme.geojson);
            if (!parsedGeojson) {
              return; // Ignora geometrias inválidas
            }

            // Log da geometria processada
            // console.log(`[useMapClick] Geometria do tema ${theme.nome_tema} processada:`, {
            //   tipo: parsedGeojson.type,
            //   temCoordinates: !!parsedGeojson.coordinates,

            // Adiciona à lista por fonte (para compatibilidade)
            geometrias.push({
              geojson: parsedGeojson,
              source: 'CAR',
              layerType: 'area',
              properties: {
                nome_tema: theme.nome_tema,
                cod_imovel: carItem.cod_imovel || '',
              },
            });

            // Adiciona à estrutura por tema (para o LayerGroupManager)
            const nomeTema = theme.nome_tema || 'ÁREA DO IMÓVEL';
            if (!temaGeometrias[nomeTema]) {
              temaGeometrias[nomeTema] = [];
            }
            temaGeometrias[nomeTema].push(parsedGeojson);
          } catch (_error) {
            /* no-op */
          }
        });
      });
    }

    // Extrai geometrias do SIGEF (usando nova estrutura de API)
    if (properties.sigef && Array.isArray(properties.sigef)) {
      properties.sigef.forEach((sigefItem, _index) => {
        if (!sigefItem || !sigefItem.parcels || !Array.isArray(sigefItem.parcels)) {
          return;
        }

        // Processar cada parcela do item SIGEF
        sigefItem.parcels.forEach((parcel, _parcelIndex) => {
          if (!parcel || !parcel.parcela_co || !parcel.geojson) {
            return;
          }

          try {
            // Converte string GeoJSON para objeto, se necessário
            const parsedGeojson = GeometryService.processGeoJSON(parcel.geojson);
            if (!parsedGeojson) {
              return; // Ignora geometrias inválidas
            }

            // Adiciona à lista por fonte
            geometrias.push({
              geojson: parsedGeojson,
              source: 'SIGEF',
              layerType: 'area',
              properties: {
                codigo_imo: sigefItem.codigo_imo || '',
                parcela_co: parcel.parcela_co || '',
              },
            });

            // Adiciona à estrutura por tema (para o LayerGroupManager)
            const temaNome = 'SIGEF';
            if (!temaGeometrias[temaNome]) {
              temaGeometrias[temaNome] = [];
            }
            temaGeometrias[temaNome].push(parsedGeojson);
          } catch (_error) {
            /* no-op */
          }
        });
      });
    }

    return {
      geometries: geometrias,
      themeGeometries: temaGeometrias,
    };
  }
}
